// FlightSuppliersMapping.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { Search } from "@mui/icons-material";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  getSupplierDetailsAPI,
  updateSupplierAPI,
  updateAllSuppliersAPI,
} from "../operations/supplierService";

/**
 * NOTE:
 * - This component expects the API base to be the exact endpoints you provided.
 * - Keep your existing theme; I kept the sx colors you used in previous code.
 */

interface ApiSupplierItem {
  id: number;
  supplier_name: string;
  supplier_source: string; // API returns uppercase "API", "AMADEUS", etc.
  is_active: 0 | 1;
  office_id: string | null;
  environment: string;
  modified_date: string;
}

interface Supplier {
  id: number;
  name: string;
  supplierSource: string; // keep as API returns (uppercase) per your choice 'A'
  active: boolean;
  officeId: string | null;
  environment: string;
  modifiedDate: string; // raw string from API
}

const FlightSuppliersMapping: React.FC = () => {
  const [supplierData, setSupplierData] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("All");
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMsg, setSnackbarMsg] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"enableAll" | "disableAll" | null>(null);
  const [confirmSingleOpen, setConfirmSingleOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);


  const formatDate = (date: string) => {
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };



  // Fetch supplier configs from API and map to UI model
  const fetchSupplierConfigs = async () => {
    try {
      setLoading(true);
      const response = await getSupplierDetailsAPI();

      const rows: ApiSupplierItem[] =
        response?.data?.body ?? response?.data ?? [];

      const mapped: Supplier[] = rows.map((item) => ({
        id: item.id,
        name: item.supplier_name,
        supplierSource: item.supplier_source, // keep exact casing from API (A)
        active: item.is_active === 1,
        officeId: item.office_id ?? null,
        environment: item.environment,
        modifiedDate: item.modified_date,
      }));

      setSupplierData(mapped);
    } catch (err) {
      console.error("Failed to fetch supplier configs:", err);
      showSnackbar("Failed to fetch supplier data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSupplierConfigs();
  }, []);

  // Snackbar helper
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning" = "success"
  ) => {
    setSnackbarMsg(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  // Filtering logic uses supplierData (from API)
  const filteredSuppliers = supplierData.filter((s) => {
    const term = search.trim().toLowerCase();
    const nameMatch = s.name?.toLowerCase().includes(term);
    const sourceMatch = s.supplierSource?.toLowerCase().includes(term);
    const typeMatch =
      filterType === "All" || s.supplierSource.toLowerCase() === filterType.toLowerCase();

    return (term === "" ? true : nameMatch || sourceMatch) && typeMatch;
  });

  // counts
  const totalCount = supplierData.length;
  const activeCount = supplierData.filter((s) => s.active).length;
  const inactiveCount = totalCount - activeCount;

  // Individual toggle handler -> POST /supplier/update
  // API payload expects: { supplier_name, supplier_source, id }
  const handleToggle = async (supplier: Supplier) => {
    // optimistic UI: flip state locally to provide immediate feedback,
    // but we'll reconcile with server response (server returns new_is_active)
    const optimistic = supplierData.map((s) =>
      s.id === supplier.id ? { ...s, active: !s.active } : s
    );
    setSupplierData(optimistic);

    try {
      const payload = {
        supplier_name: supplier.name,
        supplier_source: supplier.supplierSource,
        id: supplier.id,
      };
      const res = await updateSupplierAPI(payload);

      // If response contains new_is_active, update that row precisely
      const data = res?.data;
      if (data && typeof data.new_is_active !== "undefined") {
        const statusText = data.new_is_active === 1 ? "activated" : "deactivated";
        // setSupplierData((prev) =>
        //   prev.map((s) =>
        //     s.id === data.id ? { ...s, active: data.new_is_active === 1 } : s
        //   )
        // );
        const formattedName = supplier.name.charAt(0).toUpperCase() + supplier.name.slice(1);
        showSnackbar(`${formattedName} has been ${statusText}`, "success");
        fetchSupplierConfigs();
      } else {
        // fallback: refetch list to ensure correctness
        await fetchSupplierConfigs();
        const formattedName = supplier.name.charAt(0).toUpperCase() + supplier.name.slice(1);
        showSnackbar(`${formattedName} status updated`, "success");
      }
    } catch (err) {
      console.error("Failed to update supplier:", err);
      // revert optimistic change
      setSupplierData((prev) => prev.map((s) => (s.id === supplier.id ? supplier : s)));
      const formattedName = supplier.name.charAt(0).toUpperCase() + supplier.name.slice(1);
      showSnackbar(`Failed to update ${formattedName}`, "error");
    }
  };

  // Enable/Disable All handler -> POST /supplier/update/all
  // Payload: { action: "enableAll" } or { action: "disableAll" }
  const handleEnableDisableAll = async (action: "enableAll" | "disableAll") => {
    setLoading(true);
    try {
      const payload = { action };
      const res = await updateAllSuppliersAPI(payload);

      if (res?.success) {
        // After bulk update, re-fetch to get real state
        await fetchSupplierConfigs();
        const msg =
          action === "enableAll"
            ? "All suppliers activated successfully"
            : "All suppliers deactivated successfully";
        showSnackbar(msg, "success");
      } else {
        showSnackbar("Failed to update all suppliers", "error");
      }
    } catch (err) {
      console.error("Failed to update all suppliers:", err);
      showSnackbar("Failed to update all suppliers", "error");
    } finally {
      setLoading(false);
    }
  };

  // UI helpers: chip colors (kept your colors from previous code)
  const getSourceChipStyles = (source: string) => {
    // keep exact colors you used earlier; API returns "API" or "AMADEUS"
    const isApi = source.toUpperCase() === "API";
    return {
      fontWeight: 600,
      textTransform: "capitalize" as const,
      color: isApi ? "#7e22ce" : "#c2410c",
      backgroundColor: isApi ? "#f3e8ff" : "#ffedd5",
      borderRadius: "25px",
      fontSize: "0.85rem",
      px: 1.2,
      py: 0.5,
    };
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Supplier Activation Management
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3,1fr)" },
          gap: 2,
          mb: 5,
        }}
        className="statusShowBox"
      >
        <Card sx={{ bgcolor: "#eff6ff" }}>
          <CardContent>
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#2563eb" }}>
              Total Suppliers
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: "24px", color: "#1d4ed8" }}>
              {totalCount}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: "#f0fdf4" }}>
          <CardContent>
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#16a34a" }}>
              Active
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: "24px", color: "#15803d" }}>
              {activeCount}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: "#f8fafc" }}>
          <CardContent>
            <Typography sx={{ fontWeight: 500, fontSize: "12px", color: "#475569" }}>
              Inactive
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: "24px", color: "#334155" }}>
              {inactiveCount}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <TextField
          variant="outlined"
          placeholder="Search by supplier name or supplier source..."
          fullWidth
          size="small"
          sx={{ backgroundColor: "#fff" }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
          }}
        />

        <FormControl sx={{ minWidth: 250 }}>
          <Select
            className='leftFilterBox'
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            displayEmpty
            size="small"
          >
            <MenuItem value="All">All Supplier Sources</MenuItem>
            <MenuItem value="API">API</MenuItem>
            <MenuItem value="AMADEUS">AMADEUS</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Supplier Name</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Supplier Source</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Environment</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Office ID</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Last Modified At</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Action</strong>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && supplierData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredSuppliers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No suppliers found
                </TableCell>
              </TableRow>
            ) : (
              filteredSuppliers.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell sx={{ textTransform: "capitalize" }}>{supplier.name}</TableCell>
                  <TableCell align="center" className="chipFilledRevert">
                    <Chip label={supplier.supplierSource} sx={getSourceChipStyles(supplier.supplierSource)} />
                  </TableCell>

                  

                  <TableCell align="center">
  {supplier.environment
    ? supplier.environment.charAt(0).toUpperCase() + supplier.environment.slice(1).toLowerCase()
    : ""}
</TableCell>
<TableCell align="center">
                    {supplier.officeId ? supplier.officeId : "-"}
                  </TableCell>
                  <TableCell align="center">
                    {supplier.modifiedDate ? formatDate(supplier.modifiedDate) : "-"}
                  </TableCell>
                  
                  {/* <TableCell align="center" sx={{ minWidth: 150 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                      sx={{
                        color: supplier.active ? "success.main" : "text.secondary",
                      }}
                    >
                      <PowerSettingsNewIcon fontSize="small" />
                      <Typography fontWeight={500}>
                        {supplier.active ? "Active" : "Not Active"}
                      </Typography>
                    </Box>
                  </TableCell> */}

                  <TableCell align="center">
                    <Switch
                      checked={supplier.active}
                      color="success"
                      onChange={() => {
  setSelectedSupplier(supplier);
  setConfirmSingleOpen(true);
}}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bulk Actions */}
      <Box display="flex" justifyContent="flex-start" gap={2} mt={3}>
        <Button
          variant="contained"
          color="success"
          startIcon={<PowerSettingsNewIcon />}
          // onClick={() => handleUpdateAll("enableAll")}
          onClick={() => {
    setConfirmAction("enableAll");
    setConfirmOpen(true);
  }}
          disabled={loading}
        >
          Activate All
        </Button>

        <Button
          variant="contained"
          color="inherit"
          startIcon={<CancelIcon />}
          // onClick={() => handleUpdateAll("disableAll")}
          onClick={() => {
    setConfirmAction("disableAll");
    setConfirmOpen(true);
  }}
          disabled={loading}
        >
          Deactivate All
        </Button>
      </Box>

      {/* Snackbar (Top Right) */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "50% !important",
          left: "50% !important",
          transform: "translate(-50%, -50%) !important",
          '& .MuiSnackbarContent-root': {
            textAlign: 'center',
            minWidth: '200px',
          },
        }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} 
        variant="filled"
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '1rem'
        }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
  <DialogTitle>Confirmation</DialogTitle>
  <DialogContent>
    {confirmAction === "enableAll"
      ? "Are you sure you want to activate all suppliers?"
      : "Are you sure you want to deactivate all suppliers?"}
  </DialogContent>
  <DialogActions>
    <Button variant="outlined" onClick={() => setConfirmOpen(false)}>Cancel</Button>
    <Button
      variant="contained"
      color={confirmAction === "disableAll" ? "error" : "success"}
      onClick={() => {
        handleEnableDisableAll(confirmAction!);
        setConfirmOpen(false);
      }}
    >
      Yes
    </Button>
  </DialogActions>
</Dialog>
<Dialog
  open={confirmSingleOpen}
  onClose={() => setConfirmSingleOpen(false)}
>
  <DialogTitle>Confirmation</DialogTitle>
  <DialogContent>
  {selectedSupplier && (
    <span>
      {selectedSupplier.active ? "Are you sure you want to Deactivate " : "Are you sure you want to Activate "}
      <strong>{selectedSupplier.name.charAt(0).toUpperCase() + selectedSupplier.name.slice(1)}</strong>?
    </span>
  )}
</DialogContent>

  <DialogActions>
    <Button variant="outlined" onClick={() => setConfirmSingleOpen(false)}>
      Cancel
    </Button>

    <Button
      variant="contained"
      color={selectedSupplier?.active ? "error" : "success"}
      onClick={() => {
        if (selectedSupplier) {
          handleToggle(selectedSupplier);
        }
        setConfirmSingleOpen(false);
      }}
    >
      Yes
    </Button>
  </DialogActions>
</Dialog>

    </Box>
    
  );
};

export default FlightSuppliersMapping;
